<template>
  <div class="col-sm-12">
    <div class="card shadow-sm p-3">

      <!-- Filters Section -->
      <div class="mb-3">
        <div class="row">
          <!-- Distributor Dropdown -->
          <div class="col-md-3 mb-3">
            <label for="distributor" class="form-label fw-semibold">Distributor</label>
            <select v-model="filters.distributor" id="distributor" class="form-select" @change="onDistributorChange"
              :disabled="isDistributorLocked">
              <option value="">Select Distributor</option>

              <option v-for="dist in distributors" :key="dist.profile_id ?? dist.id"
                :value="String(dist.profile_id ?? dist.id)">
                {{ dist.name ?? dist.first_name ?? dist.full_name ?? 'Unknown' }}
              </option>
            </select>
          </div>

          <!-- Date Field -->
          <div class="col-md-3 mb-3">
            <label for="date" class="form-label fw-semibold">Date</label>
            <input type="date" v-model="filters.date" id="date" class="form-control" />
          </div>

          <!-- Vehicle Dropdown -->
          <div class="col-md-3 mb-3">
            <label for="vehicle" class="form-label fw-semibold">Vehicle</label>
            <select :key="filters.distributor" v-model="filters.vehicle" id="vehicle" class="form-select"
              :disabled="vehiclesLoading || !filters.distributor">
              <option value="" v-if="!vehiclesLoading">Please select vehicle</option>
              <option value="" v-if="vehiclesLoading">Loading vehicles...</option>

              <!-- ✅ All Vehicles option -->
              <option :value="0" v-if="!vehiclesLoading">All Vehicles</option>

              <option v-for="vehicle in vehicles" :key="vehicle.id" :value="vehicle.id">
                {{ vehicle.vehicle_no }}
              </option>
            </select>
          </div>

          <!-- Search Button -->
          <div class="col-md-3 d-flex align-items-end mb-3">
            <button class="btn btn-primary w-100" @click="search" :disabled="loading">
              <i class="fa fa-search me-1" :class="{ 'fa-spin': loading }"></i>
              {{ loading ? 'Searching...' : 'Search' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Loader -->
      <div v-if="pageLoading || loading" class="text-center my-4">
        <i class="fa fa-spinner fa-spin fa-2x text-primary"></i>
        <p class="mt-2">
          {{ pageLoading ? 'Loading page...' : 'Loading report data...' }}
        </p>
      </div>


      <!-- Results Table -->
      <div v-if="!loading && reportData.length > 0" class="table-responsive mt-3">
        <table class="table table-bordered table-hover align-middle">
          <thead class="table-light">
            <tr>
              <th class="text-center">#</th>
              <th>Dealer Name</th>
              <th>Invoice No</th>
              <th v-for="item in uniqueItems" :key="item.key" class="text-center">
                {{ item.name }}
              </th>
              <th class="text-end">Cash Amount</th>
              <th class="text-end">Cheque Amount</th>
              <th class="text-end">Bank Transfer</th>
              <th class="text-end">Credit Amount</th>
              <th class="text-end">Invoice Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in reportData" :key="index">
              <td class="text-center">{{ index + 1 }}</td>
              <td>{{ row.dealer_name }}</td>
              <td>{{ row.invoice_no }}</td>

              <td v-for="item in uniqueItems" :key="item.key" class="text-center">
                {{ getItemQuantity(row, item.key) }}
              </td>

              <td class="text-end">{{ formatCurrency(row.cash_amount) }}</td>
              <td class="text-end">{{ formatCurrency(row.cheque_amount) }}</td>
              <td class="text-end">{{ formatCurrency(row.bank_transfer_amount) }}</td>
              <td class="text-end">{{ formatCurrency(row.credit_amount) }}</td>
              <td class="text-end fw-bold">{{ formatCurrency(row.invoice_total) }}</td>
            </tr>
          </tbody>

          <!-- Totals Row -->
          <tfoot class="table-secondary fw-bold">
            <tr>
              <td colspan="3" class="text-end"><strong>Grand Totals:</strong></td>
              <td v-for="item in uniqueItems" :key="item.key" class="text-center">
                <strong>{{ getTotalForItem(item.key) }}</strong>
              </td>
              <td class="text-end">{{ formatCurrency(totals.total_cash) }}</td>
              <td class="text-end">{{ formatCurrency(totals.total_cheque) }}</td>
              <td class="text-end">{{ formatCurrency(totals.total_bank_transfer) }}</td>
              <td class="text-end">{{ formatCurrency(totals.total_credit) }}</td>
              <td class="text-end">{{ formatCurrency(totals.total_invoice_amount) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- No Data Message -->
      <div v-else-if="!loading && searched" class="text-center mt-4">
        <i class="fa fa-inbox fa-3x text-muted mb-3"></i>
        <p class="text-muted">No data found for the selected filters.</p>
      </div>

      <!-- Export Button -->
      <div v-if="!loading && reportData.length > 0" class="mt-3">
        <button class="btn btn-success" @click="exportToExcel">
          <i class="fa fa-download me-1"></i> Export to Excel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchDayEndReport, fetchDistributorsList, fetchVehicleListrep } from "@/services/dataService";

export default {
  name: "DayEndReport",
  data() {
    return {
      profile_id: localStorage.getItem("profile_id") || null,

      ALL_VEHICLES: "__all_vehicles__",
      userLabel: "",
      roleKey: "OTHER",
      isDistributorLocked: false,

      filters: {
        distributor: "",
        date: this.getDefaultDate(),
        vehicle: "",
      },
      distributors: [],
      vehicles: [],
      reportData: [],
      uniqueItems: [],
      totals: {
        total_invoices: 0,
        total_invoice_amount: 0,
        total_cash: 0,
        total_cheque: 0,
        total_bank_transfer: 0,
        total_credit: 0,
      },
      loading: false,
      vehiclesLoading: false,
      searched: false,
      pageLoading: false,
    };
  },

  created() {
    this.initRoleFromStorage();
    this.initPage();
  },

  methods: {
    getDefaultDate() {
      const today = new Date();
      return today.toISOString().split("T")[0];
    },

    // ✅ role detection
    initRoleFromStorage() {
      const labelFromStorage =
        localStorage.getItem("user_label") ||
        localStorage.getItem("userLabel") ||
        localStorage.getItem("role") ||
        "";

      this.userLabel = String(labelFromStorage);
      const labelUpper = String(this.userLabel || "").toUpperCase().trim();

      if (
        labelUpper === "DISTRIBUTOR" ||
        labelUpper.startsWith("DISTRIBUTOR") ||
        labelUpper === "DIS" ||
        labelUpper.startsWith("DIS_")
      ) this.roleKey = "DIS";
      else if (labelUpper === "ASM" || labelUpper.startsWith("ASM_")) this.roleKey = "ASM";
      else if (labelUpper === "FSM" || labelUpper.startsWith("FSM_")) this.roleKey = "FSM";
      else this.roleKey = "OTHER";
    },

    async initPage() {
      this.pageLoading = true;
      await this.$nextTick(); // ✅ allow loader to paint

      try {
        await this.loadDistributors();
        // loadDistributors already loads vehicles for distributor login (your code)
      } finally {
        this.pageLoading = false;
      }
    },


    isDistributorUser() {
      return this.roleKey === "DIS";
    },

    /**
     * Converts item name to backend key format
     * Backend uses exact lowercase with underscores, preserving numbers and special chars
     */
    // convertToBackendKey(itemName) {
    //   return itemName
    //     .toLowerCase()
    //     .replace(/ /g, "_")
    //     .replace(/\(/g, "")
    //     .replace(/\)/g, "")
    //     .replace(/\\/g, "")
    //     .replace(/"/g, "")
    //     .replace(/__+/g, "_");
    // },


    convertToBackendKey(itemName) {
      return String(itemName ?? "")
        .toLowerCase()
        .replace(/ /g, "_"); // match backend exactly
    },


    async loadDistributors() {
      try {
        const token = localStorage.getItem("token_set");
        const response = await fetchDistributorsList(this.profile_id, token);
        this.distributors = response?.data?.data || [];

        // ✅ Distributor login: prefill + lock + load vehicles
        if (this.isDistributorUser() && this.profile_id) {
          this.filters.distributor = String(this.distributors[0]?.profile_id);
          this.isDistributorLocked = true;

          // programmatic set doesn't trigger @change, so load vehicles manually
          await this.loadVehicles(this.filters.distributor);
        } else {
          this.isDistributorLocked = false;
        }
      } catch (error) {
        console.error("Error loading distributors:", error);
        this.$toast?.error?.("Failed to load distributors");
      }
    },

    onDistributorChange() {
      this.filters.vehicle = "0";
      this.vehicles = [];
      this.reportData = [];
      this.uniqueItems = [];
      this.searched = false;

      if (this.filters.distributor) {
        this.loadVehicles(this.filters.distributor);
      }
    },

    async loadVehicles(distributor_profile_id) {
      this.vehiclesLoading = true;
      this.vehicles = [];
      try {
        const token = localStorage.getItem("token_set");
        const response = await fetchVehicleListrep(distributor_profile_id, token);
        let vehiclesData = response?.data?.data || [];

        this.vehicles = vehiclesData.map(v => ({
          id: v.id ?? v.vehicle_id ?? v.profile_id,
          vehicle_no: v.vehicle_no ?? v.name ?? "Unknown",
        }));
      } catch (error) {
        console.error("Error loading vehicles:", error);
        this.$toast?.error?.("Failed to load vehicles");
      } finally {
        this.vehiclesLoading = false;
      }
    },

    async search() {
      if (!this.filters.distributor) {
        this.$toast?.warning?.("Please select a distributor");
        return;
      }

      if (this.filters.vehicle === "" || this.filters.vehicle === null || this.filters.vehicle === undefined) {
        this.$toast?.warning?.("Please select a vehicle");
        return;
      }


      if (!this.filters.date) {
        this.$toast?.warning?.("Please select a date");
        return;
      }

      this.loading = true;
      await this.$nextTick();

      this.reportData = [];
      this.uniqueItems = [];
      this.searched = false;

      try {
        const token = localStorage.getItem("token_set");

        const params = {
          distributor_profile_id: this.filters.distributor,
          date: this.filters.date,

          // ✅ send 0 for All Vehicles
          vehicle_id: this.filters.vehicle === "0" ? 0 : Number(this.filters.vehicle),
        };


        const response = await fetchDayEndReport(token, params);

        // if (response?.data?.status) {
        //   this.uniqueItems = (response.data.items || []).map(item => ({
        //     key: this.convertToBackendKey(item.item_name),
        //     name: item.item_name
        //   }));

        //   this.reportData = response.data.data || [];

        //   this.totals = {
        //     ...response.data.totals,
        //     total_invoices: this.reportData.length,
        //     total_invoice_amount: response.data.totals?.total_invoice_amount || 0,
        //     total_cash: response.data.totals?.total_cash || 0,
        //     total_cheque: response.data.totals?.total_cheque || 0,
        //     total_bank_transfer: response.data.totals?.total_bank_transfer || 0,
        //     total_credit: response.data.totals?.total_credit || 0,
        //   };

        //   this.uniqueItems.forEach(item => {
        //     const totalKey = `total_${item.key}`;
        //     this.totals[totalKey] = this.reportData.reduce(
        //       (sum, row) => sum + (parseFloat(row[item.key]) || 0),
        //       0
        //     );
        //   });

        //   this.searched = true;

        //   if (!this.reportData.length) {
        //     this.$toast?.info?.("No data found for the selected criteria");
        //   } else {
        //     this.$toast?.success?.(`Report loaded successfully with ${this.reportData.length} invoices`);
        //   }
        // } 

        // else {
        //   this.$toast?.error?.(response?.data?.message || "Failed to load report");
        // }


        if (response?.data?.status) {
          const allItems = (response.data.items || []).map(item => ({
            key: this.convertToBackendKey(item.item_name),
            name: item.item_name
          }));
          
          this.reportData = response.data.data || [];

          const itemTotals = {};
          for (const it of allItems) itemTotals[it.key] = 0;

          for (const row of this.reportData) {
            for (const it of allItems) {
              itemTotals[it.key] += parseFloat(row[it.key]) || 0;
            }
          }

          const EPS = 0.000001;
          this.uniqueItems = allItems.filter(it => Math.abs(itemTotals[it.key]) > EPS);

          this.totals = {
            ...response.data.totals,
            total_invoices: this.reportData.length,
            total_invoice_amount: response.data.totals?.total_invoice_amount || 0,
            total_cash: response.data.totals?.total_cash || 0,
            total_cheque: response.data.totals?.total_cheque || 0,
            total_bank_transfer: response.data.totals?.total_bank_transfer || 0,
            total_credit: response.data.totals?.total_credit || 0,
          };

          this.uniqueItems.forEach(item => {
            this.totals[`total_${item.key}`] = itemTotals[item.key] || 0;
          });

          this.searched = true;

          if (!this.reportData.length) {
            this.$toast?.info?.("No data found for the selected criteria");
          } else {
            this.$toast?.success?.(`Report loaded successfully with ${this.reportData.length} invoices`);
          }
        } else {
          this.$toast?.error?.(response?.data?.message || "Failed to load report");
        }
      } catch (error) {
        console.error("Error loading report:", error);
        this.$toast?.error?.("Error loading report. Please try again.");
      } finally {
        this.loading = false;
      }
    },

    getItemQuantity(row, itemKey) {
      const value = row[itemKey] ?? 0;
      return parseFloat(value).toFixed(2);
    },

    formatCurrency(amount) {
      const num = parseFloat(amount) || 0;
      return num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    getTotalForItem(itemKey) {
      const totalKey = `total_${itemKey}`;
      if (this.totals[totalKey] !== undefined) {
        return parseFloat(this.totals[totalKey]).toFixed(2);
      }
      return this.reportData
        .reduce((sum, row) => sum + (parseFloat(row[itemKey]) || 0), 0)
        .toFixed(2);
    },

    exportToExcel() {
      try {
        const XLSX = require("xlsx");

        const totalCols = 3 + this.uniqueItems.length + 5;
        const padTo = (arr) => {
          const out = Array.isArray(arr) ? [...arr] : [];
          while (out.length < totalCols) out.push("");
          return out.slice(0, totalCols);
        };

        // ---- FILTERS (Top Metadata) ----
        let distributorName = "All Distributors";
        if (this.filters.distributor) {
          const selectedDist = this.distributors.find(
            d => String(d.profile_id ?? d.id) === String(this.filters.distributor)
          );
          if (selectedDist) {
            distributorName =
              selectedDist.name ||
              selectedDist.first_name ||
              selectedDist.full_name ||
              `Distributor #${this.filters.distributor}`;
          }
        }

        let vehicleName = "All Vehicles";
        if (this.filters.vehicle && Number(this.filters.vehicle) !== 0) {
          const selectedVehicle = this.vehicles.find(
            v => String(v.id) === String(this.filters.vehicle)
          );
          if (selectedVehicle) {
            vehicleName = selectedVehicle.vehicle_no || `Vehicle #${this.filters.vehicle}`;
          }
        }

        const filterRows = [
          ["Day End Report"],
          [],
          ["Distributor", distributorName],
          ["Vehicle",     vehicleName],
          ["Date",        this.filters.date || "-"],
          [],
        ];

        // ---- HEADERS ----
        const headerRow = [
          "#",
          "Dealer Name",
          "Invoice No",
          ...this.uniqueItems.map(item => item.name),
          "Cash Amount",
          "Cheque Amount",
          "Bank Transfer",
          "Credit Amount",
          "Invoice Total",
        ];

        // ---- DATA ROWS ----
        const dataRows = this.reportData.map((row, index) => {
          const rowData = [
            index + 1,
            row.dealer_name  || "",
            row.invoice_no   || "",
          ];

          this.uniqueItems.forEach(item => {
            rowData.push(parseFloat(this.getItemQuantity(row, item.key)) || 0);
          });

          rowData.push(
            parseFloat(row.cash_amount          || 0),
            parseFloat(row.cheque_amount        || 0),
            parseFloat(row.bank_transfer_amount || 0),
            parseFloat(row.credit_amount        || 0),
            parseFloat(row.invoice_total        || 0)
          );

          return rowData;
        });

        // ---- TOTALS ROW ----
        const totalsRow = ["", "", "TOTALS"];

        this.uniqueItems.forEach(item => {
          totalsRow.push(parseFloat(this.getTotalForItem(item.key)) || 0);
        });

        totalsRow.push(
          parseFloat(this.totals.total_cash             || 0),
          parseFloat(this.totals.total_cheque           || 0),
          parseFloat(this.totals.total_bank_transfer    || 0),
          parseFloat(this.totals.total_credit           || 0),
          parseFloat(this.totals.total_invoice_amount   || 0)
        );

        // ---- FOOTER (Bottom Metadata) ----
        const firstName    = localStorage.getItem("first_name") || "";
        const lastName     = localStorage.getItem("last_name")  || "";
        const userFullName = (firstName + " " + lastName).trim() || "-";
        const generatedAt  = new Date().toLocaleString("en-US", { hour12: false });

        const footerRows = [
          [],
          ["Generated by", userFullName],
          ["Generated at", generatedAt],
        ];

        // ---- BUILD FINAL AOA ----
        const aoa = [
          ...filterRows.map(padTo),
          padTo(headerRow),
          ...dataRows.map(padTo),
          padTo(totalsRow),
          ...footerRows.map(padTo),
        ];

        const ws = XLSX.utils.aoa_to_sheet(aoa);

        // ---- MERGES ----
        const merges = [];
        // Merge title row across first 4 cols
        merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } });
        ws["!merges"] = merges;

        // Make title bold
        if (ws["A1"]) ws["A1"].s = { font: { bold: true, sz: 14 } };

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Day_End_Report");
        XLSX.writeFile(wb, `Day_End_Report_${this.filters.date}.xlsx`);

        this.$toast?.success?.("Report exported successfully");
      } catch (error) {
        console.error("Export error:", error);
        this.$toast?.error?.("Failed to export report. Please try again.");
      }
    },
  }
};
</script>

<style scoped>
.card {
  border-radius: 10px;
}

.form-label {
  font-size: 0.9rem;
}

.table th,
.table td {
  font-size: 0.85rem;
  vertical-align: middle;
}

.table tfoot {
  background-color: #f8f9fa !important;
  font-weight: 600;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

@media print {

  .btn,
  .mb-3 {
    display: none !important;
  }

  .table {
    font-size: 0.75rem;
  }
}
</style>